import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { ApiError, EXPENSE_PERIOD_LABELS, EXPENSE_PERIODS } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  costForSubmit,
  ExpenseCatalogFormSchema,
  type ExpenseCatalogFormValues,
  isExpenseCatalogFormField,
} from '@/components/custom/expense-catalog-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { expenseCatalogsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Create AND edit share this one sheet — pass `item` (the loaded row) to
 * edit it in place, omit it to create. Both the list's row action and the
 * detail page's Edit button open this instead of the old
 * `/setup/expense-catalogs/$id/edit` full-page route, which existed only to
 * duplicate this form and has been removed.
 *
 * Deliberately NOT built on `CatalogFormSheet`. That component's Zod schema is
 * a fixed `name / code / description / kind:boolean` shape shared by four
 * setup screens; expressing a money field and a three-value enum through it
 * would mean widening that schema plus two more opt-in config props and two
 * more gates in its error router — the parameter explosion CONTRIBUTING G10
 * says to avoid, taken on by four screens that gain nothing from it.
 *
 * What IS shared is everything that matters for consistency: the same shadcn
 * Sheet primitives, the same `useUnsavedChangesGuard`, the same field/error
 * markup, and the same `ApiError.fields` → `form.setError` routing. Visually
 * and behaviorally this is the same sheet; it just owns its own field set.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit (or `null`) to create; pass the loaded row to edit it in place. */
  item?: ExpenseCatalog | null;
}

const EMPTY: ExpenseCatalogFormValues = {
  name: '',
  description: '',
  cost: '',
  variable: false,
  period: 'monthly',
};

export function ExpenseCatalogFormSheet({ open, onOpenChange, item = null }: Props) {
  const isEdit = item != null;
  const queryClient = useQueryClient();

  const form = useForm<ExpenseCatalogFormValues>({
    resolver: zodResolver(ExpenseCatalogFormSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    if (item) {
      form.reset({
        name: item.name,
        description: item.description ?? '',
        // The API returns the canonical `numeric(12,2)` string; feed it to the
        // input verbatim rather than through Number, so 90.00 stays 90.00.
        // `null` is the API's "variable, no standing price" — the form's own
        // blank-cost convention (see `ExpenseCatalogFormSchema`'s comment).
        cost: item.cost ?? '',
        variable: item.variable,
        period: item.period,
      });
    } else {
      form.reset(EMPTY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const mutation = useMutation({
    mutationFn: (values: ExpenseCatalogFormValues) => {
      const payload = {
        name: values.name,
        description: values.description,
        cost: costForSubmit(values),
        variable: values.variable,
        period: values.period,
      };
      return isEdit && item
        ? expenseCatalogsApi.patch(item.id, payload)
        : expenseCatalogsApi.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Expense item saved' : 'Expense item created');
      queryClient.invalidateQueries({ queryKey: queryKeys.expenseCatalogs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        let hadFieldHit = false;
        for (const fe of err.fields) {
          if (!isExpenseCatalogFormField(fe.path)) continue;
          form.setError(fe.path, { message: fe.message });
          hadFieldHit = true;
        }
        if (hadFieldHit) {
          toast.error('Please fix the highlighted fields');
        } else {
          // A field error we don't render (or a whole-row error) would
          // otherwise show "fix the highlighted fields" with nothing
          // highlighted. Surface it as a toast instead.
          const first = err.fields[0];
          toast.error(first ? `${first.path}: ${first.message}` : err.message);
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const errors = form.formState.errors;
  const isVariable = form.watch('variable');

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{item ? `Edit ${item.name}` : 'Add expense item'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Changes apply immediately.'
                : 'A pass-through item you can bill on to a customer. Name must be unique within your tenant.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="QuickBooks Online Plus"
                  aria-invalid={Boolean(errors.name)}
                  {...form.register('name')}
                />
                {errors.name?.message ? (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={3}
                  placeholder="What this expense covers."
                  aria-invalid={Boolean(errors.description)}
                  {...form.register('description')}
                />
                {errors.description?.message ? (
                  <p className="text-xs text-destructive">{errors.description.message}</p>
                ) : null}
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="variable">Variable amount</Label>
                  <p className="text-xs text-muted-foreground">
                    The amount differs every time this is incurred, so there is no standing price to
                    record here.
                  </p>
                </div>
                <Switch
                  id="variable"
                  checked={isVariable}
                  onCheckedChange={(on) =>
                    form.setValue('variable', on, { shouldDirty: true, shouldValidate: true })
                  }
                />
              </div>

              {/* The Cost field is REMOVED, not disabled, when the switch is
                  on: a greyed-out box still reads as "a value belongs here and
                  you may not set it", which is the opposite of the truth. What
                  the user typed before flipping is preserved in form state, so
                  flipping back restores it rather than making them retype. */}
              {isVariable ? null : (
                <div className="space-y-1.5">
                  <Label htmlFor="cost">Cost</Label>
                  {/* Deliberately `type="text"` + `inputMode="decimal"`, not
                      `type="number"`. A number input with `min`/`step` hands
                      validation to the browser: `90.005` is a stepMismatch, so
                      the browser cancels form submission and shows its own
                      tooltip — react-hook-form never runs and our inline
                      "at most 2 decimal places" message is unreachable (G12).
                      Same reasoning as `CurrencyInput`, which also avoids the
                      +/- spinners. Zod owns the rules; the input just collects
                      text. */}
                  <Input
                    id="cost"
                    type="text"
                    inputMode="decimal"
                    placeholder="90.00"
                    aria-invalid={Boolean(errors.cost)}
                    {...form.register('cost')}
                  />
                  {errors.cost?.message ? (
                    <p className="text-xs text-destructive">{errors.cost.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Amount charged each period.</p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="period">Period</Label>
                <Select
                  value={form.watch('period')}
                  onValueChange={(v) =>
                    form.setValue('period', v as ExpenseCatalogFormValues['period'], {
                      shouldDirty: true,
                    })
                  }
                >
                  <SelectTrigger
                    id="period"
                    className="w-full"
                    aria-invalid={Boolean(errors.period)}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_PERIODS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {EXPENSE_PERIOD_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.period?.message ? (
                  <p className="text-xs text-destructive">{errors.period.message}</p>
                ) : null}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
